/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable } from 'rxjs';
import { scan, filter, pluck, withLatestFrom } from 'rxjs/operators';
import { sampleWithSource } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { FancyAction } from './FancyAction';

//both generic types are necessary when using a creator, otherwise one is fine (possibly none if typescript is smart enough)
export function monitorSubList<Item, MappedItem = Item>(
	observable: Observable<FancyAction<Item>>,
	compFunc: (action: FancyAction<Item>) => boolean,
	isRemoveAction: (type: string) => boolean,
	customCreator?: (action: FancyAction) => MappedItem
): Observable<Map<string, MappedItem>> {
	return observable.pipe(
		scan(
			(state: { changed: boolean; list: Map<string, any> }, action: FancyAction) => {
				let changed = false;
				const compRes = compFunc(action);
				const remove = isRemoveAction(action.type);
				if (remove) {
					changed = state.list.has(action.key);
					if (changed) {
						state.list.delete(action.key);
					}
				} else if (compRes && !state.list.has(action.key)) {
					let thing;
					if (customCreator) {
						thing = customCreator(action);
					} else {
						thing = action.payload;
					}
					state.list.set(action.key, thing);
					changed = true;
				} else if (!compRes && state.list.has(action.key)) {
					state.list.delete(action.key);
					changed = true;
				}
				state.changed = changed;
				return state;
			},
			{ changed: false, list: new Map() }
		),
		filter((state) => state.changed),
		pluck('list')
	);
}

/**
 * Can pair this with monitorSubList to create an observable that emits on changes to selection AND subsequent changes to the selected items
 * ```
 * organizationsApi.data.editList$.pipe(
 * 	includeChangesInSubListMonitoring(organizationsApi.data.rawEvent$)
 * )
 * ```
 */
export function includeChangesInSubListMonitoring<V, T extends Map<string, V>>(
	rawEvent$: Observable<FancyAction>
) {
	return (sourceOb$: Observable<T>) => {
		const notifier = rawEvent$.pipe(
			withLatestFrom(sourceOb$),
			filter(([action, stuffBeingEdited]) => stuffBeingEdited.has(action.key))
		);

		return sourceOb$.pipe(sampleWithSource(notifier));
	};
}
